import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AppConfig } from '@shared/types';
import { api } from './api';

// Same defaults as the server, so the first paint (before /api/config answers) already shows the brand.
export const DEFAULT_CONFIG: AppConfig = {
  appName: 'Quiz Dayro',
  chapterName: 'GDG Baroda',
  creditName: 'JT',
  creditUrl: 'https://akajammythakkar.dev',
  publicUrl: null,
  database: 'pglite',
  maxPlayers: 300,
  googleSignIn: false,
  mcpUrl: null,
  signInRestricted: false,
  // An older server doesn't send it: no share controls against it (e.g. during a rollover).
  sharing: false,
};

/** Exported so tests can render pages with a given config. */
export const ConfigContext = createContext<AppConfig>(DEFAULT_CONFIG);

/**
 * False until /api/config has answered (or failed): until then `googleSignIn` is only the default,
 * so pages must not say sign-in is off. True without a ConfigProvider (tests give a config).
 */
export const ConfigLoadedContext = createContext<boolean>(true);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api
      .config()
      .then((c) => {
        if (cancelled || !c || typeof c !== 'object') return;
        setConfig({ ...DEFAULT_CONFIG, ...c });
        const full = { ...DEFAULT_CONFIG, ...c };
        const subtitle = brandSubtitle(full);
        document.title = subtitle ? `${full.appName} · ${subtitle}` : full.appName;
      })
      .catch(() => {
        /* keep defaults */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <ConfigContext.Provider value={config}>
      <ConfigLoadedContext.Provider value={loaded}>{children}</ConfigLoadedContext.Provider>
    </ConfigContext.Provider>
  );
}

/** "by JT × GDG Baroda", "by JT", "GDG Baroda" or '' depending on what is configured. */
export function brandSubtitle({ creditName, chapterName }: Pick<AppConfig, 'creditName' | 'chapterName'>): string {
  if (creditName && chapterName) return `by ${creditName} × ${chapterName}`;
  if (creditName) return `by ${creditName}`;
  return chapterName;
}

export function useAppConfig(): AppConfig {
  return useContext(ConfigContext);
}

/** Whether the server's config has arrived (see ConfigLoadedContext). */
export function useConfigLoaded(): boolean {
  return useContext(ConfigLoadedContext);
}

/** Base URL players should open to join (no trailing slash). */
export function useJoinBaseUrl(): string {
  const { publicUrl } = useAppConfig();
  return (publicUrl ?? window.location.origin).replace(/\/+$/, '');
}
